import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import * as api from "api/api";
import {
	LeadContactActivityResource,
	LeadContactReportResource,
	ContactEventTypeResource,
	LeadContactReportQuery,
} from "api/resource/lead-contact-report";
import { loginReset } from "./authentication";

/**
 * Leads & Contacts report (StatsContact.cfm) — MAX-3887. Read-only: one page of the report plus its
 * total count for server-driven paging, and the Type dropdown list. Mirrors the fetch-thunk pattern
 * in redux/slice/leads.ts.
 */
export interface ReportsLeadsState {
	loading?: boolean;
	error?: string | null;
	loaded: boolean;
	types: ContactEventTypeResource[];
	activities: LeadContactActivityResource[];
	totalCount: number;
	filter: LeadContactReportQuery | null;
}

const initialState: ReportsLeadsState = {
	loaded: false,
	types: [],
	activities: [],
	totalCount: 0,
	filter: null,
};

const fetchContactEventTypes = createAsyncThunk(
	"reportsLeads/fetchTypes",
	async (payload, thunkAPI) => {
		const result = api.getContactEventTypes();
		result
			.then((types) => thunkAPI.dispatch(contactEventTypesLoaded(types)))
			.catch((error) => thunkAPI.dispatch(reportsLeadsFailed(error)));
		return result;
	}
);

const fetchLeadContactReport = createAsyncThunk(
	"reportsLeads/fetchReport",
	async (payload: LeadContactReportQuery, thunkAPI) => {
		thunkAPI.dispatch(reportsLeadsLoading());
		const result = api.getLeadContactReport(payload);
		result
			.then((report) => thunkAPI.dispatch(reportLoaded({ report, filter: payload })))
			.catch((error) => thunkAPI.dispatch(reportsLeadsFailed(error)));
		return result;
	}
);

export const reportsLeadsSlice = createSlice({
	name: "reportsLeads",
	initialState,
	reducers: {
		reportsLeadsLoading(state) {
			state.loading = true;
		},
		contactEventTypesLoaded(state, action: PayloadAction<ContactEventTypeResource[]>) {
			state.error = null;
			state.types = action.payload;
		},
		reportLoaded(
			state,
			action: PayloadAction<{ report: LeadContactReportResource; filter: LeadContactReportQuery }>
		) {
			state.loading = false;
			state.error = null;
			state.loaded = true;
			state.activities = action.payload.report.activities;
			state.totalCount = action.payload.report.totalCount;
			state.filter = action.payload.filter;
		},
		reportsLeadsFailed(state, action: PayloadAction<any>) {
			state.loading = false;
			state.error = action.payload && action.payload.message;
		},
	},
	extraReducers(builder) {
		builder.addCase(loginReset, (state) => {
			return {
				...initialState,
				error: state.error,
			};
		});
	},
});

export const {
	reportsLeadsLoading,
	contactEventTypesLoaded,
	reportLoaded,
	reportsLeadsFailed,
} = reportsLeadsSlice.actions;

export { fetchContactEventTypes, fetchLeadContactReport };
